// Public business profile + booking flow. No authentication required.
//
// Customers land here from a link the business shares. If the business has
// in-app bookings enabled, we show:
//   - a fleet-picker with vehicle COVER PHOTOS (so the customer sees what
//     they're selecting),
//   - a date/time picker that visibly disables ranges already taken by
//     other bookings or covered by an active vehicle incident,
//   - the existing availability + booking submission flow.
//
// Demo businesses (`company.is_demo`) get a sticky red banner + first-visit
// modal so visitors cannot mistake them for a real rental business.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Car, Calendar, ShieldCheck, CheckCircle2, AlertTriangle, ArrowLeft, PlayCircle,
} from "lucide-react";
import DemoBusinessGuard from "../components/DemoBusinessGuard";
import ExternalWebsiteButton from "../components/ExternalWebsiteButton";
import { VehicleCoverThumb } from "../components/PublicFleetPhotos";
import BookingTermsPanel from "../components/BookingTermsPanel";
import TrustBadges from "../components/TrustBadges";
import DrivingModeSelector from "../components/drivers/DrivingModeSelector";
import WhoWillDrive from "../components/renter-drivers/WhoWillDrive";


const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const fmt = (n) => "₱" + Number(n || 0).toLocaleString("en-PH");

const isoLocal = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toISOString();
};

// Convert ISO → "YYYY-MM-DDTHH:MM" for the <input type=datetime-local> min/max
// attributes. We deliberately use local time so the browser-rendered min/max
// matches what the user is picking.
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtRange = (p) => {
  const s = new Date(p.start);
  const e = new Date(p.end);
  const sameDay =
    s.getFullYear() === e.getFullYear() &&
    s.getMonth() === e.getMonth() &&
    s.getDate() === e.getDate();
  const dateOpts = { month: "short", day: "numeric" };
  const timeOpts = { hour: "numeric", minute: "2-digit" };
  if (sameDay) {
    return `${s.toLocaleDateString(undefined, dateOpts)}, ${s.toLocaleTimeString(undefined, timeOpts)} → ${e.toLocaleTimeString(undefined, timeOpts)}`;
  }
  return `${s.toLocaleDateString(undefined, dateOpts)} ${s.toLocaleTimeString(undefined, timeOpts)} → ${e.toLocaleDateString(undefined, dateOpts)} ${e.toLocaleTimeString(undefined, timeOpts)}`;
};

const overlapsBlocked = (start, end, periods) => {
  if (!start || !end || !periods?.length) return false;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  for (const p of periods) {
    const ps = new Date(p.start).getTime();
    const pe = new Date(p.end).getTime();
    if (s < pe && e > ps) return true;
  }
  return false;
};


export default function PublicBusiness() {
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [blockedPeriods, setBlockedPeriods] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  // Book Again (Phase 2E): a completed booking in the customer portal can
  // link here with ?name=&email=&phone=&pickup=&vehicle=<id>&repeat_of=<id>
  // to prefill the form. Dates and availability are ALWAYS re-picked and
  // re-checked live below: we never trust stale pricing/availability from
  // the old booking.
  const repeatOfBookingId = searchParams.get("repeat_of") || null;
  const [form, setForm] = useState({
    name: searchParams.get("name") || "",
    email: searchParams.get("email") || "",
    phone: searchParams.get("phone") || "",
    notes: "",
    pickup: searchParams.get("pickup") || "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [driving, setDriving] = useState({ mode: "self_drive", passenger_count: "", destination: "" });
  const [whoDrives, setWhoDrives] = useState({ renterDriving: true, details: null });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const idempotencyKeyRef = useRef(null);

  // A changed request is a new operation; retries of an unchanged request
  // retain the same key so network retries cannot create duplicate bookings.
  useEffect(() => {
    idempotencyKeyRef.current = null;
  }, [selectedVehicleId, start, end, form, acceptedTerms, repeatOfBookingId]);

  const reloadProfile = useCallback(() => axios.get(`${API}/public/business/${companyId}`)
    .then((r) => {
      setData(r.data);
      const prefVehicle = searchParams.get("vehicle");
      if (prefVehicle && (r.data.vehicles || []).some((v) => v.id === prefVehicle)) {
        setSelectedVehicleId(prefVehicle);
      }
      return r.data;
    })
    .catch((e) => { toast.error(e?.response?.data?.detail || "Could not load business"); return null; }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId]);

  useEffect(() => {
    reloadProfile().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const company = data?.company;
  const enabled = data?.enable_in_app_bookings;
  const vehicles = useMemo(() => data?.vehicles || [], [data]);
  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === selectedVehicleId),
    [vehicles, selectedVehicleId],
  );

  // Fetch blocked periods whenever the customer picks a different vehicle.
  // We reset the date inputs so they don't carry over a range that was OK
  // for the previous vehicle but conflicts with the new one's bookings.
  useEffect(() => {
    if (!selectedVehicleId) {
      setBlockedPeriods([]);
      return;
    }
    setBlockedLoading(true);
    setAvailability(null);
    axios
      .get(`${API}/public/business/${companyId}/vehicles/${selectedVehicleId}/blocked-periods`)
      .then((r) => setBlockedPeriods(r.data?.periods || []))
      .catch(() => setBlockedPeriods([]))
      .finally(() => setBlockedLoading(false));
  }, [companyId, selectedVehicleId]);

  const blockedConflict = useMemo(
    () => overlapsBlocked(start, end, blockedPeriods),
    [start, end, blockedPeriods],
  );

  // For the <input type=datetime-local min=…>, never let the customer
  // pick a moment in the past.
  const nowMin = useMemo(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  }, []);

  const checkAvailability = useCallback(async () => {
    if (!selectedVehicleId || !start || !end) {
      toast.error("Pick a vehicle and a start/end date first");
      return;
    }
    if (blockedConflict) {
      toast.error("These dates conflict with an unavailable period for this vehicle.");
      return;
    }
    setChecking(true);
    setAvailability(null);
    try {
      const body = {
        item_id: selectedVehicleId,
        start_datetime: isoLocal(start),
        end_datetime: isoLocal(end),
      };
      const { data: a } = await axios.post(
        `${API}/public/business/${companyId}/availability`, body,
      );
      setAvailability(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check availability");
    } finally {
      setChecking(false);
    }
  }, [companyId, selectedVehicleId, start, end, blockedConflict]);

  const submit = async (e) => {
    e.preventDefault();
    if (!company?.current_terms) {
      toast.error("Online booking is not available yet because this business has not published its ConfirmPH booking agreement.");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Please accept the booking terms to continue");
      return;
    }
    setSubmitting(true);
    try {
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = globalThis.crypto?.randomUUID?.()
          || `booking-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      const { data: r } = await axios.post(`${API}/public/business/${companyId}/book`, {
        item_id: selectedVehicleId,
        start_datetime: isoLocal(start), end_datetime: isoLocal(end),
        customer_name: form.name, customer_email: form.email,
        customer_phone: form.phone || null,
        pickup_location: form.pickup || null,
        customer_notes: form.notes || null,
        accepted_terms: true,
        accepted_terms_version_id: company.current_terms.id,
        repeat_of_booking_id: repeatOfBookingId,
        rental_service_type: driving.mode,
        ...(driving.mode === "self_drive" ? {
          renter_is_driving: whoDrives.renterDriving,
          ...(whoDrives.details ? { primary_driver: whoDrives.details } : {}),
        } : {}),
        ...(driving.mode === "with_driver" ? {
          passenger_count: Number(driving.passenger_count) || null,
          destination: driving.destination || null,
        } : {}),
      }, {
        headers: { "Idempotency-Key": idempotencyKeyRef.current },
      });
      setResult(r);
    } catch (err) {
      if (err?.response?.status === 409) idempotencyKeyRef.current = null;
      const detail = err?.response?.data?.detail;
      if (detail?.code === "terms_version_stale") {
        // Do NOT silently accept the new version: clear prior acceptance
        // and force the customer to review whatever is current now.
        setAcceptedTerms(false);
        idempotencyKeyRef.current = null;
        await reloadProfile();
        toast.error("The booking terms were just updated: please review the new terms and confirm again.");
      } else if (detail?.code === "no_published_terms") {
        await reloadProfile();
        toast.error(detail.message || "This business hasn't published booking terms yet.");
      } else {
        toast.error((typeof detail === "string" ? detail : detail?.message) || "Could not submit booking");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Business not found.</div>;

  if (result) return (
    <>
      <DemoBusinessGuard isDemo={!!company?.is_demo} companyId={companyId} />
      {company?.is_demo && (
        <nav data-testid="public-business-scenarios-nav-result"
             className="sticky top-9 sm:top-10 z-40 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <span className="text-xs text-slate-600 hidden sm:inline">Demo business</span>
            <div className="flex-1" />
            <Link to="/demo-business/scenarios"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#1B4332] hover:bg-emerald-900 text-white text-sm font-semibold transition-colors">
              <PlayCircle className="w-4 h-4" />
              Demo Scenarios
            </Link>
          </div>
        </nav>
      )}
      <ResultPage result={result} company={company} />
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <DemoBusinessGuard isDemo={!!company?.is_demo} companyId={companyId} />

      {/* Sticky demo-only nav: always-visible entry point to the public
          Scenarios catalog.  Sits directly below the red demo banner so a
          rental-business owner never has to hunt for it. */}
      {company?.is_demo && (
        <nav data-testid="public-business-scenarios-nav"
             className="sticky top-9 sm:top-10 z-40 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <Link to="/" className="text-xs text-slate-500 hover:text-slate-800 hidden sm:inline">
              ConfirmPH
            </Link>
            <span className="text-slate-300 hidden sm:inline">·</span>
            <span className="text-xs text-slate-600 hidden sm:inline">Demo business</span>
            <div className="flex-1" />
            <Link to="/demo-business/scenarios"
                  data-testid="public-business-scenarios-nav-cta"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#1B4332] hover:bg-emerald-900 text-white text-sm font-semibold transition-colors">
              <PlayCircle className="w-4 h-4" />
              Demo Scenarios
            </Link>
          </div>
        </nav>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4" /> ConfirmPH
        </Link>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid="public-business-card">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 inline-flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Listed on ConfirmPH
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold">{company?.name}</h1>
          {company?.city && <p className="text-sm text-slate-600">{company.city}</p>}
          {(company?.phone || company?.email) && (
            <p className="text-sm text-slate-600 mt-1">
              {company.phone && <>📞 {company.phone}</>}{company.phone && company.email && " · "}
              {company.email && <>✉️ {company.email}</>}
            </p>
          )}
          {company?.external_website?.url && (
            <div className="mt-4">
              <ExternalWebsiteButton
                companyId={companyId}
                externalWebsite={company.external_website}
              />
            </div>
          )}

          <div className="mt-4">
            <TrustBadges companyId={companyId} compact />
          </div>

          {/* Demo-only "Scenarios" CTA: lets business owners self-explore
              the same interactive ConfirmPH scenarios promoters use, without
              promoter guidance.  Only visible on demo businesses. */}
          {company?.is_demo && (
            <Link to="/demo-business/scenarios"
                  data-testid="public-business-scenarios-cta"
                  className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 transition-colors p-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white grid place-items-center flex-shrink-0">
                  <PlayCircle className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-emerald-900 leading-tight">Scenarios</div>
                  <div className="text-xs text-emerald-800 leading-snug mt-0.5">
                    See how ConfirmPH handles real rental situations, step by step.
                  </div>
                </div>
              </div>
              <span className="text-xs font-semibold text-emerald-800 whitespace-nowrap hidden sm:inline">Try it →</span>
            </Link>
          )}
        </div>

        {repeatOfBookingId && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="public-book-again-banner">
            Booking again: your details are pre-filled below. Please pick new dates; availability and pricing are checked fresh.
          </div>
        )}

        {!enabled && (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="public-disabled-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium">In-app booking is not available for this business</div>
                <p className="mt-1 text-amber-900/90">{data?.disabled_reason || "Please contact this business directly to arrange your rental."}</p>
                <Link
                  to={`/rentals/${companyId}`}
                  data-testid="public-disabled-inquiry-cta"
                  className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-900 text-amber-50 hover:bg-amber-950 text-xs font-medium"
                >
                  Send rental inquiry instead
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle picker */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-lg font-semibold">Our fleet</h2>
          <p className="text-xs text-slate-500 mt-0.5">Tap a vehicle to see its availability.</p>
          {vehicles.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No vehicles to show yet.</p>
          ) : (
            <ul className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="public-vehicle-list">
              {vehicles.map((v) => (
                <li key={v.id}>
                  <VehicleOption
                    v={v}
                    companyId={companyId}
                    selected={selectedVehicleId === v.id}
                    disabled={!enabled}
                    onSelect={() => { setSelectedVehicleId(v.id); setDriving({ mode: "self_drive", passenger_count: "", destination: "" }); }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {enabled && selectedVehicle && (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="public-booking-form">
            <h2 className="font-display text-lg font-semibold">Book {selectedVehicle.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Mode: <strong>{company?.booking_mode === "automatic_hold" ? "Temporary hold" : "Request to confirm"}</strong>
            </p>

            <BlockedPeriodsList
              loading={blockedLoading}
              periods={blockedPeriods}
            />

            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <label>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Pickup</span>
                <input type="datetime-local" value={start} min={nowMin}
                        onChange={(e) => { setStart(e.target.value); setAvailability(null); }}
                        data-testid="public-start-input"
                        className={`mt-1.5 w-full px-3 py-2 rounded-lg border outline-none ${
                          blockedConflict ? "border-rose-400 bg-rose-50" : "border-slate-300"
                        }`} />
              </label>
              <label>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Return</span>
                <input type="datetime-local" value={end} min={start || nowMin}
                        onChange={(e) => { setEnd(e.target.value); setAvailability(null); }}
                        data-testid="public-end-input"
                        className={`mt-1.5 w-full px-3 py-2 rounded-lg border outline-none ${
                          blockedConflict ? "border-rose-400 bg-rose-50" : "border-slate-300"
                        }`} />
              </label>
            </div>
            {blockedConflict && (
              <div data-testid="public-blocked-conflict"
                    className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                These dates overlap an unavailable window for this vehicle. Please pick a different time range.
              </div>
            )}

            <button onClick={checkAvailability}
                    disabled={checking || blockedConflict}
                    data-testid="public-check-availability"
                    className="mt-3 px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed">
              <Calendar className="w-3.5 h-3.5" /> {checking ? "Checking…" : "Check availability & price"}
            </button>

            {availability && (
              <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${availability.available ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-rose-200 bg-rose-50 text-rose-900"}`}
                   data-testid="public-availability-result">
                {availability.available ? (
                  <>
                    Available · <strong>{fmt(availability.price_estimate)}</strong> ({availability.days} day{availability.days === 1 ? "" : "s"})
                    {availability.deposit_required > 0 && <> · Security deposit: <strong>{fmt(availability.deposit_required)}</strong></>}
                  </>
                ) : (
                  <>Not available: {availability.reason}</>
                )}
              </div>
            )}

            {availability?.available && (
              <form onSubmit={submit} className="mt-5 grid gap-3" data-testid="public-customer-fields">
                <DrivingModeSelector api={API} companyId={companyId} vehicle={selectedVehicle}
                                     start={start ? isoLocal(start) : null} end={end ? isoLocal(end) : null}
                                     value={driving} onChange={setDriving} />
                {driving.mode === "self_drive" && (
                  <WhoWillDrive companyId={companyId} vehicle={selectedVehicle}
                                start={start ? isoLocal(start) : null} end={end ? isoLocal(end) : null}
                                renterName={form.name} value={whoDrives} onChange={setWhoDrives} />
                )}
                <Input label="Your name" required value={form.name}
                        onChange={(v) => setForm({ ...form, name: v })}
                        testId="public-name-input" />
                <Input label="Email" required type="email" value={form.email}
                        onChange={(v) => setForm({ ...form, email: v })}
                        testId="public-email-input" />
                <Input label="Phone (optional)" value={form.phone}
                        onChange={(v) => setForm({ ...form, phone: v })}
                        testId="public-phone-input" />
                <Input label="Pickup location (optional)"
                        value={form.pickup} placeholder={company?.default_pickup_location || ""}
                        onChange={(v) => setForm({ ...form, pickup: v })} />
                <Input label="Notes (optional)" rows={2} value={form.notes}
                        onChange={(v) => setForm({ ...form, notes: v })} />

                {(company?.business_terms || company?.cancellation_policy) && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                    {company.business_terms && (
                      <>
                        <strong>The business’s own policies:</strong>
                        <p className="whitespace-pre-line mt-0.5">{company.business_terms}</p>
                      </>
                    )}
                    {company.cancellation_policy && (
                      <>
                        <strong className="block mt-2">Cancellation:</strong>
                        <p className="whitespace-pre-line mt-0.5">{company.cancellation_policy}</p>
                      </>
                    )}
                    <p className="text-[10px] text-slate-500 mt-2">
                      ConfirmPH is the platform that helps confirm and track this booking. The rental
                      contract is between you and the business above. ConfirmPH is not the rental provider.
                    </p>
                  </div>
                )}

                {company?.current_terms ? (
                  <>
                    <BookingTermsPanel terms={company.current_terms} />
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={acceptedTerms}
                              onChange={(e) => setAcceptedTerms(e.target.checked)}
                              data-testid="public-accept-terms"
                              className="mt-0.5" />
                      <span>
                        I have read and accept the Booking Agreement (Version {company.current_terms.version_number}) above,
                        and the ConfirmPH disclaimer.
                      </span>
                    </label>
                  </>
                ) : (
                  <p className="text-xs text-rose-700" data-testid="public-no-terms-notice">
                    Online booking is not available yet because this business has not published its ConfirmPH booking agreement.
                  </p>
                )}

                <button type="submit" disabled={submitting || !acceptedTerms || !company?.current_terms}
                        data-testid="public-submit-btn"
                        className="px-5 py-3 rounded-lg btn-brand font-medium disabled:opacity-60">
                  {submitting ? "Submitting…" : (company?.booking_mode === "automatic_hold" ? "Create hold" : "Send booking request")}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VehicleOption({ v, companyId, selected, disabled, onSelect }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      data-testid={`public-vehicle-${v.id}`}
      className={`w-full text-left rounded-xl border overflow-hidden transition flex flex-col ${
        selected ? "border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-50"
                 : "border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300"
      } ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
    >
      <div className="relative" data-testid={`public-vehicle-image-wrap-${v.id}`}>
        <VehicleCoverThumb
          companyId={companyId}
          vehicleId={v.id}
          coverId={v.cover_photo_id}
        />
        {selected && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
            <CheckCircle2 className="w-3 h-3" /> Selected
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2">
          <Car className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{v.name}</div>
            <div className="text-xs text-slate-500 truncate">
              {[v.category, v.transmission, v.seats && `${v.seats} seats`, v.fuel_type]
                .filter(Boolean).join(" · ") || "-"}
            </div>
            <div className="text-sm font-mono mt-1">
              {fmt(v.price_per_day)}<span className="text-slate-500"> / day</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function BlockedPeriodsList({ loading, periods }) {
  if (loading) {
    return (
      <div className="mt-3 text-xs text-slate-500" data-testid="public-blocked-loading">
        Checking this vehicle&apos;s availability…
      </div>
    );
  }
  if (!periods.length) {
    return (
      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-900"
            data-testid="public-blocked-none">
        No unavailable windows on file for this vehicle in the next 4 months.
      </div>
    );
  }
  // Cap the rendered list: beyond ~6 windows the panel gets noisy.
  const visible = periods.slice(0, 6);
  const more = periods.length - visible.length;
  return (
    <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50/70 px-3 py-2 text-xs text-rose-900"
          data-testid="public-blocked-periods">
      <div className="font-semibold flex items-center gap-1 text-rose-800">
        <AlertTriangle className="w-3.5 h-3.5" /> Unavailable for this vehicle
      </div>
      <ul className="mt-1 space-y-0.5" data-testid="public-blocked-list">
        {visible.map((p, i) => (
          <li key={i} className="font-mono">{fmtRange(p)}</li>
        ))}
      </ul>
      {more > 0 && <div className="mt-1 italic">+ {more} more period{more === 1 ? "" : "s"}</div>}
      <div className="mt-1 text-[10px] text-rose-700/80 not-italic">
        These windows are blocked. Please choose a time outside the dates above.
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, required, rows, testId }) {
  const Tag = rows ? "textarea" : "input";
  return (
    <label>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <Tag
        type={type} required={required} value={value}
        rows={rows} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none"
      />
    </label>
  );
}

function ResultPage({ result, company }) {
  const isHold = !!result.is_hold;
  return (
    <div className="min-h-screen bg-[#FAFAFA] grid place-items-center p-4">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="public-result-page">
        <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h1 className="mt-3 font-display text-2xl font-bold">{isHold ? "Booking on hold" : "Request submitted"}</h1>
        <p className="mt-2 text-sm text-slate-600">{result.message}</p>
        {result.driver_message && <p className="mt-1 text-sm text-slate-700" data-testid="public-driver-message">{result.driver_message}</p>}
        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
          <div><span className="text-slate-500">Reference:</span> <strong className="font-mono">{result.booking_reference}</strong></div>
          <div><span className="text-slate-500">Status:</span> {isHold ? "Temporary hold" : "Awaiting business review"}</div>
          {isHold && result.hold_expires_at && (
            <div><span className="text-slate-500">Hold expires:</span> {new Date(result.hold_expires_at).toLocaleString()}</div>
          )}
          {result.price_estimate ? <div><span className="text-slate-500">Price estimate:</span> ₱{result.price_estimate.toLocaleString("en-PH")}</div> : null}
          {result.deposit_required ? <div><span className="text-slate-500">Deposit required:</span> ₱{result.deposit_required.toLocaleString("en-PH")}</div> : null}
        </div>
        <Link to={`/business/${company?.id || ""}`} className="mt-5 inline-block text-sm text-[#1B4332] hover:underline">
          Book again
        </Link>
      </div>
    </div>
  );
}
